// Lucka 5: Heta arbeten som eget tillstånd med giltighetstid, brandvakt och efterbevakning.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import DocumentHeader from "./DocumentHeader";
import { Flame, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

interface Props {
  projectId: string;
}

interface Permit {
  id: string;
  location: string;
  work_description: string;
  contractor: string | null;
  permit_issuer: string | null;
  hot_work_certificate_ref: string | null;
  fire_watch_name: string | null;
  fire_watch_after_minutes: number;
  valid_from: string | null;
  valid_to: string | null;
  precautions: string[];
  status: string;
  notes: string | null;
}

const PRECAUTIONS = [
  "Brandsläckare på plats",
  "Brandfarligt material avlägsnat",
  "Täckning av öppningar och genomföringar",
  "Larm och utrymningsvägar kontrollerade",
  "Brandvakt utsedd under arbetet",
  "Efterbevakning planerad",
];

const toLocalInput = (v: string | null) => (v ? new Date(v).toISOString().slice(0, 16) : "");
const fmt = (v: string | null) => (v ? new Date(v).toLocaleString("sv-SE") : "–");

const HotWorkPermits: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Permit[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("hot_work_permits")
      .select(
        "id, location, work_description, contractor, permit_issuer, hot_work_certificate_ref, fire_watch_name, fire_watch_after_minutes, valid_from, valid_to, precautions, status, notes"
      )
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });
    if (error) toast.error("Kunde inte läsa tillstånden");
    setRows(
      ((data as unknown as Permit[] | null) ?? []).map((r) => ({
        ...r,
        precautions: Array.isArray(r.precautions) ? r.precautions : [],
      }))
    );
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("hot_work_permits").insert({
      project_id: projectId,
      location: "Ny plats",
      work_description: "Beskriv arbetet som ger gnistor eller värme",
      created_by: auth?.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte skapa tillståndet");
    load();
  };

  const save = async (id: string, patch: Partial<Permit>) => {
    const { error } = await supabase
      .from("hot_work_permits")
      .update(patch as never)
      .eq("id", id);
    if (error) return toast.error("Kunde inte spara");
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("hot_work_permits").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const isExpired = (p: Permit) =>
    p.status === "active" && p.valid_to && new Date(p.valid_to).getTime() < Date.now();

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="16b"
        title="Heta arbeten – tillstånd"
        description="Svetsning, skärning, slipning och annat som ger gnistor eller värme kräver ett eget tillstånd med brandvakt och efterbevakning."
        icon={Flame}
        actions={
          <Button onClick={add}>
            <Plus className="mr-2 h-4 w-4" /> Nytt tillstånd
          </Button>
        }
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga tillstånd registrerade. Skapa ett innan heta arbeten påbörjas.
          </CardContent>
        </Card>
      ) : (
        rows.map((p) => (
          <Card key={p.id}>
            <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
              <div className="space-y-1">
                <CardTitle className="text-base">{p.location}</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={p.status === "active" ? "default" : p.status === "closed" ? "outline" : "secondary"}>
                    {p.status === "active" ? "Aktivt" : p.status === "closed" ? "Avslutat" : "Utkast"}
                  </Badge>
                  {isExpired(p) && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" /> Giltighetstiden har gått ut
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    Gäller {fmt(p.valid_from)} – {fmt(p.valid_to)}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="icon" aria-label="Ta bort tillstånd" onClick={() => remove(p.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>Plats</Label>
                  <Input
                    defaultValue={p.location}
                    onBlur={(e) => save(p.id, { location: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Entreprenör</Label>
                  <Input
                    defaultValue={p.contractor ?? ""}
                    onBlur={(e) => save(p.id, { contractor: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Arbetsbeskrivning</Label>
                <Textarea
                  defaultValue={p.work_description}
                  onBlur={(e) => save(p.id, { work_description: e.target.value })}
                />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>Tillståndsansvarig</Label>
                  <Input
                    defaultValue={p.permit_issuer ?? ""}
                    onBlur={(e) => save(p.id, { permit_issuer: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Certifikat heta arbeten</Label>
                  <Input
                    defaultValue={p.hot_work_certificate_ref ?? ""}
                    onBlur={(e) => save(p.id, { hot_work_certificate_ref: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Brandvakt</Label>
                  <Input
                    defaultValue={p.fire_watch_name ?? ""}
                    onBlur={(e) => save(p.id, { fire_watch_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Efterbevakning (minuter)</Label>
                  <Input
                    type="number"
                    defaultValue={p.fire_watch_after_minutes}
                    onBlur={(e) =>
                      save(p.id, { fire_watch_after_minutes: Number(e.target.value) || 60 })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label>Giltigt från</Label>
                  <Input
                    type="datetime-local"
                    defaultValue={toLocalInput(p.valid_from)}
                    onBlur={(e) =>
                      save(p.id, {
                        valid_from: e.target.value ? new Date(e.target.value).toISOString() : null,
                      })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label>Giltigt till</Label>
                  <Input
                    type="datetime-local"
                    defaultValue={toLocalInput(p.valid_to)}
                    onBlur={(e) =>
                      save(p.id, {
                        valid_to: e.target.value ? new Date(e.target.value).toISOString() : null,
                      })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Säkerhetsåtgärder före start</Label>
                <div className="grid gap-2 md:grid-cols-2">
                  {PRECAUTIONS.map((item) => {
                    const checked = p.precautions.includes(item);
                    return (
                      <label key={item} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = v
                              ? [...p.precautions, item]
                              : p.precautions.filter((x) => x !== item);
                            save(p.id, { precautions: next });
                          }}
                        />
                        {item}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <Label>Anteckningar</Label>
                <Textarea
                  defaultValue={p.notes ?? ""}
                  onBlur={(e) => save(p.id, { notes: e.target.value })}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {p.status !== "active" && (
                  <Button
                    variant="secondary"
                    onClick={() => save(p.id, { status: "active" })}
                    disabled={p.precautions.length < 3}
                  >
                    Aktivera tillstånd
                  </Button>
                )}
                {p.status === "active" && (
                  <Button variant="outline" onClick={() => save(p.id, { status: "closed" })}>
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Avsluta efter efterbevakning
                  </Button>
                )}
                {p.precautions.length < 3 && p.status !== "active" && (
                  <span className="self-center text-xs text-muted-foreground">
                    Kryssa i minst tre säkerhetsåtgärder innan tillståndet kan aktiveras.
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default HotWorkPermits;
